import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Shield } from 'lucide-react';
import { CONTACT_EMAIL, CONTACT_LOCATION } from '../config/legal';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#00D47E]/10 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#00D47E]" />
            </div>
            <h1 className="font-outfit text-3xl md:text-4xl font-bold text-black">Privacy Policy</h1>
          </div>
          <p className="text-gray-500 text-sm mb-12">Last updated: August 2026</p>

          <div className="space-y-10 text-gray-700 leading-relaxed">
            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">1. Who We Are</h2>
              <p>
                Levr ("we", "us", "our") operates levr.nz, a vehicle finance origination platform based in Auckland, New Zealand. We help consumers assess their eligibility for vehicle and personal finance, and connect them with licensed NZ lenders.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">2. Information We Collect</h2>
              <p className="mb-3">When you use our eligibility assessment and finance application services, we may collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Identity information</strong> — name, date of birth, phone number, email address</li>
                <li><strong>Financial information</strong> — income, employment details, assets, liabilities, and living situation</li>
                {/* Phrased conditionally on purpose. Document upload, ID verification and selfie
                    capture sit behind ENABLE_PHASE2_VERIFICATION and are switched OFF: the public
                    funnel asks for none of them today. Describing them as things we currently
                    collect overstates what the site does; deleting the clause would understate it
                    the moment the flag is turned on. This wording is accurate either way. */}
                <li><strong>Documents, only where you are asked for them</strong> — if you use our document upload or identity verification steps, that may include bank statements, an ID document (driver&rsquo;s licence or passport) and a selfie. The online eligibility check does not ask for any of these.</li>
                <li><strong>Technical information</strong> — browser type, device, and usage data to improve our service</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">3. How We Use Your Information</h2>
              <p className="mb-3">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Assess your eligibility and generate an approval likelihood score</li>
                <li>Extract financial data from your bank statements using AI processing, where you have provided them</li>
                <li>Verify your identity using an ID document and selfie, where those steps apply</li>
                <li>Submit your application to lenders you choose to apply with</li>
                <li>Connect you with a finance specialist if you request a callback</li>
                <li>Improve our services and eligibility models</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">4. Document Processing</h2>
              <p>
                Bank statements and ID documents are processed in memory using AI to extract relevant financial and identity data. We do not permanently store the original document files. Extracted data (such as income figures and identity details) is stored securely to support your application.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">5. Credit Checks</h2>
              <p>
                Our eligibility assessment does <strong>not</strong> perform a credit check and has no impact on your credit score. A credit check is only performed by a lender after you choose to formally apply and accept an offer. We will always make this clear before any credit inquiry occurs.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">6. Sharing With Third Parties</h2>
              <p className="mb-3">We share your information only in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Lenders</strong> — when you choose to submit a formal application, your details are shared with the lender(s) you select</li>
                {/* Was "OpenAI", which was simply wrong: document extraction runs on Anthropic's
                    Claude (`backend/document_processor.py`, `ANTHROPIC_API_KEY`). Naming the wrong
                    processor in a privacy policy misstates who holds a customer's bank statements
                    and ID — corrected 2026-08-26. */}
                <li><strong>AI processing</strong> — documents are sent to our AI provider (Anthropic) for data extraction, processed under their data processing agreement</li>
                <li><strong>Legal obligations</strong> — where required by law or regulation</li>
              </ul>
              <p className="mt-3">We never sell your personal information to third parties.</p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">7. Cookies and Website Analytics</h2>
              <p className="mb-3">
                We use Google Analytics to understand how people find and move through this website — which pages are visited, how long people stay, and roughly where visitors come from. This helps us improve the site.
              </p>
              <p className="mb-3">
                Google Analytics sets cookies in your browser and collects your IP address, which Google anonymises. It tells us about traffic patterns, <strong>not</strong> about you individually: we do not connect analytics data to your name, your enquiry, or your application.
              </p>
              <p>
                <strong>We honour "Do Not Track".</strong> If your browser sends that signal, we collect no analytics at all — Google Analytics ignores it by default, so this is something we chose to respect. You can also block cookies, use an ad blocker, or install Google's <a href="https://tools.google.com/dlpage/gaoptout" className="text-[#00D47E] underline" target="_blank" rel="noopener noreferrer">opt-out browser add-on</a>. None of this affects your ability to use the site or apply for finance.
              </p>
              <p>
                We never load analytics on our sign-in pages, so nothing about your login is ever sent to Google.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">8. Data Retention</h2>
              <p>
                We retain your application data for as long as necessary to provide our services and meet our legal obligations. You can request deletion of your data at any time by contacting us.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">9. Your Rights Under the NZ Privacy Act 2020</h2>
              <p className="mb-3">Under the Privacy Act 2020, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Complain to the Office of the Privacy Commissioner if you believe we have breached your privacy</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">10. Security</h2>
              <p>
                We use industry-standard security measures including encryption in transit and at rest, session-based authentication, and secure document handling. Uploaded documents are processed in memory and are not written to permanent storage.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">11. Contact Us</h2>
              <p>
                If you have questions about this privacy policy or want to exercise your privacy rights, contact us at:
              </p>
              <p className="mt-2">
                <strong>Email:</strong> {CONTACT_EMAIL}<br />
                <strong>Location:</strong> {CONTACT_LOCATION}
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
